// Dashboard de Pendências - Bloco 3
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { 
  AlertTriangle, CheckCircle, Clock, Users, BookOpen, 
  GraduationCap, TrendingUp, Target, Calendar, Brain,
  AlertCircle, Loader2
} from 'lucide-react';

const API_URL = window.location.origin;

const STATUS_COLORS = {
  lancado: '#10B981',
  pendente: '#F59E0B',
  parcial: '#3B82F6',
  atrasado: '#F97316',
  critico: '#EF4444'
};

export function DashboardPendencias({ year, trimestre }) {
  const [data, setData] = useState(null);
  const [resumoIA, setResumoIA] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [year, trimestre]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [dashRes, resumoRes] = await Promise.all([
        fetch(`${API_URL}/api/pendencias-academicas/dashboard?year=${year}&trimestre=${encodeURIComponent(trimestre)}`),
        fetch(`${API_URL}/api/pendencias-academicas/resumo-ia?year=${year}&trimestre=${encodeURIComponent(trimestre)}`)
      ]);
      
      const dashData = await dashRes.json();
      const resumoData = await resumoRes.json();
      
      setData(dashData);
      setResumoIA(resumoData);
    } catch (error) {
      console.error('Erro ao carregar dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!data) return null;

  const { kpis, pendencias_por_tipo, top_turmas_pendencias, top_professores_pendencias } = data;

  // Dados para gráfico de pizza
  const pieData = [
    { name: 'Concluído', value: kpis.total_lancado, color: '#10B981' },
    { name: 'Pendente', value: kpis.total_esperado - kpis.total_lancado, color: '#F59E0B' }
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-pendencias">
      {/* Resumo IA */}
      {resumoIA && (
        <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0 shadow-lg shadow-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Brain className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Análise Inteligente</h3>
                <p className="text-purple-100 leading-relaxed">{resumoIA.resumo}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPIs Principais */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
        {/* Percentual de Conclusão */}
        <Card className="col-span-2 bg-gradient-to-br from-green-500 to-emerald-600 text-white border-0">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm opacity-80">Conclusão</div>
                <div className="text-4xl font-bold">{kpis.percentual_conclusao}%</div>
                <Progress value={kpis.percentual_conclusao} className="mt-2 bg-white/30 h-2" />
              </div>
              <div className="p-3 bg-white/20 rounded-xl">
                <Target className="w-8 h-8" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Total de Pendências */}
        <Card className="bg-white border-purple-100 hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-purple-600 mb-1">
              <AlertCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Pendências</span>
            </div>
            <div className="text-3xl font-bold text-slate-800">{kpis.total_pendencias}</div>
            <div className="text-xs text-slate-500">registros</div>
          </CardContent>
        </Card>

        {/* Pendências Críticas */}
        <Card className="bg-red-50 border-red-200 hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-red-600 mb-1">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-xs font-medium">Críticas</span>
            </div>
            <div className="text-3xl font-bold text-red-600">{kpis.pendencias_criticas}</div>
            <div className="text-xs text-red-500">urgentes</div>
          </CardContent>
        </Card>

        {/* Turmas com Pendência */}
        <Card className="bg-white border-purple-100 hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-blue-600 mb-1">
              <BookOpen className="w-4 h-4" />
              <span className="text-xs font-medium">Turmas</span>
            </div>
            <div className="text-3xl font-bold text-slate-800">{kpis.turmas_com_pendencia}</div>
            <div className="text-xs text-slate-500">com pendência</div>
          </CardContent>
        </Card>

        {/* Turmas Completas */}
        <Card className="bg-green-50 border-green-200 hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-green-600 mb-1">
              <CheckCircle className="w-4 h-4" />
              <span className="text-xs font-medium">Completas</span>
            </div>
            <div className="text-3xl font-bold text-green-600">{kpis.turmas_completas}</div>
            <div className="text-xs text-green-500">de {kpis.total_turmas}</div>
          </CardContent>
        </Card>

        {/* Disciplinas */}
        <Card className="bg-white border-purple-100 hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-orange-600 mb-1">
              <GraduationCap className="w-4 h-4" />
              <span className="text-xs font-medium">Disciplinas</span>
            </div>
            <div className="text-3xl font-bold text-slate-800">{kpis.disciplinas_com_pendencia}</div>
            <div className="text-xs text-slate-500">com pendência</div>
          </CardContent>
        </Card>

        {/* Professores */}
        <Card className="bg-white border-purple-100 hover:shadow-md transition-shadow">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-indigo-600 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xs font-medium">Professores</span>
            </div>
            <div className="text-3xl font-bold text-slate-800">{kpis.professores_com_pendencia}</div>
            <div className="text-xs text-slate-500">com pendência</div>
          </CardContent>
        </Card>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gráfico de Pizza - Progresso Geral */}
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-purple-600" />
              Progresso Geral
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-2">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="text-sm text-slate-600">Concluído ({kpis.total_lancado})</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="text-sm text-slate-600">Pendente ({kpis.total_esperado - kpis.total_lancado})</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Pendências por Tipo de Avaliação */}
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-purple-600" />
              Por Tipo de Avaliação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[250px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pendencias_por_tipo} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis type="category" dataKey="tipo" width={100} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="total" fill="#8E52AB" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Top Turmas com Pendências */}
        <Card className="border-purple-100">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              Turmas com Mais Pendências
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {top_turmas_pendencias.map((turma, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <div className="font-medium text-slate-700 text-sm">{turma.nome}</div>
                    <div className="text-xs text-slate-500">{turma.total} pendências</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {turma.criticas > 0 && (
                      <Badge className="bg-red-500 text-xs">{turma.criticas} críticas</Badge>
                    )}
                  </div>
                </div>
              ))}
              {top_turmas_pendencias.length === 0 && (
                <div className="text-center py-4 text-slate-500">
                  <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                  <p className="text-sm">Nenhuma turma com pendências críticas!</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Professores */}
      <Card className="border-purple-100">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-purple-600" />
            Professores com Pendências
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {top_professores_pendencias.map((prof, idx) => (
              <div key={idx} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-bold">
                    {prof.nome.charAt(0)}
                  </div>
                  <div>
                    <div className="font-medium text-slate-700 text-sm truncate max-w-[120px]">{prof.nome}</div>
                    <div className="text-xs text-slate-500">{prof.total} pendências</div>
                  </div>
                </div>
                {prof.criticas > 0 && (
                  <Badge className="bg-red-100 text-red-700 text-xs">{prof.criticas} críticas</Badge>
                )}
              </div>
            ))}
            {top_professores_pendencias.length === 0 && (
              <div className="col-span-full text-center py-4 text-slate-500">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                <p className="text-sm">Todos os professores estão em dia!</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info do Trimestre */}
      <div className="text-center text-sm text-slate-500">
        <Calendar className="w-4 h-4 inline mr-1" />
        Dados do {trimestre} - {year}
      </div>
    </div>
  );
}
